import React, { useState, useMemo, useRef } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import '../styles/Reports.css';

const COLORS = {
  happiness: '#1ab02a',
  tiredness: '#ffa405',
  green: '#1ab02a',
  yellow: '#ffa405',
  red: '#ef4444',
  teams: ['#032054', '#1ab02a', '#3fbaeb', '#ffa405', '#06b6d4', '#8b5cf6', '#6366f1', '#ec4899'],
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatYTick = (value) => {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
};

function getMondayOfWeek(weeksAgo = 0) {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff - weeksAgo * 7);
  return d.toISOString().slice(0, 10);
}

function getWeekKey(ta) {
  return ta.weekStartDate?.toDate
    ? ta.weekStartDate.toDate().toISOString().slice(0, 10)
    : (ta.weekStartDate?.slice?.(0, 10) ?? '');
}

function formatWeekLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yy = String(d.getUTCFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
}

/**
 * Month bucket key for a week start date. "2025-03-17" -> "2025-03".
 * A week is attributed to the calendar month its Monday falls in.
 */
function getMonthKey(weekKey) {
  return String(weekKey).slice(0, 7);
}

/** "2025-03" -> "Mar/25" — two stacked lines in the axis tick. */
function formatMonthLabel(monthKey) {
  const [y, m] = String(monthKey).split('-');
  const name = MONTH_NAMES[Number(m) - 1] ?? m;
  return `${name}/${String(y).slice(2)}`;
}

const TIME_PERIODS = [
  { value: '10weeks', label: 'Last 10 Weeks', weeks: 10 },
  { value: '26weeks', label: 'Last 26 Weeks', weeks: 26 },
  { value: '52weeks', label: 'Last 52 Weeks', weeks: 52 },
  { value: 'all',     label: 'All Time',      weeks: null },
];

// Monthly grouping only makes sense over longer ranges (26 weeks / ~6 months+).
const MIN_WEEKS_FOR_MONTHLY = 26;

const GRANULARITIES = [
  { value: 'week',  label: 'By Week',  desc: 'One point per week start date' },
  { value: 'month', label: 'By Month', desc: 'Weeks averaged into calendar months' },
];

const PAGE_ORIENTATIONS = [
  { value: 'portrait',  label: 'Portrait',  desc: 'Standard upright A4 page' },
  { value: 'landscape', label: 'Landscape', desc: 'Wide page, compact header, 2 team charts per page' },
];

const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

/**
 * Returns XAxis config (interval, fontSize, axisHeight) scaled to data density.
 *
 * All date labels render as a stacked block sitting entirely below the axis
 * line — 3 lines (Dd / Mm / yy) for weekly, 2 lines (Mmm / yy) for monthly.
 * Because each line is short, the horizontal footprint per label is small and
 * mostly independent of how many labels are showing — so we can pack labels
 * fairly tightly without them colliding or running off the page edge.
 *
 * @param {number}  dataLength   – number of data points
 * @param {boolean} isLandscape  – landscape capture div is wider
 * @param {boolean} isTeamChart  – team charts share row with speedometers (narrower)
 * @param {boolean} isCompany    – company chart gets a slightly larger font
 * @param {number}  labelLines   – stacked lines per label (3 weekly, 2 monthly)
 */
function getXAxisConfig(dataLength, isLandscape = false, isTeamChart = false, isCompany = false, labelLines = 3) {
  // Pixel widths of the off-screen capture divs
  const captureWidth = isTeamChart
    ? (isLandscape ? 900  : 490)
    : (isLandscape ? 1400 : 850);

  // Minimum px gap between stacked-label centres. Monthly labels are wider
  // (3-letter month name) so they need more room than 2-digit weekly lines.
  const minGapPx = labelLines <= 2 ? 40 : 24;

  // How many labels can we show without overlap?
  const maxLabels = Math.max(1, Math.floor(captureWidth / minGapPx));

  // interval: skip enough ticks so shown count ≤ maxLabels
  const interval = dataLength <= maxLabels
    ? 0
    : Math.ceil(dataLength / maxLabels) - 1;

  const visibleLabels = Math.ceil(dataLength / (interval + 1));

  // Font size: be generous — these will be scaled down when embedded in the PDF
  // but the capture div renders at full pixel size so bigger = sharper output.
  let fontSize;
  if      (visibleLabels <= 5)  fontSize = isCompany ? 17.5 : 14;
  else if (visibleLabels <= 8)  fontSize = isCompany ? 16   : 13;
  else if (visibleLabels <= 12) fontSize = isCompany ? 15   : 12;
  else if (visibleLabels <= 18) fontSize = isCompany ? 13.5 : 11;
  else if (visibleLabels <= 26) fontSize = isCompany ? 12.5 : 10;
  else if (visibleLabels <= 40) fontSize = isCompany ? 12   : 9.5;
  else                          fontSize = isCompany ? 11   : 9;

  // Axis height: room for a short tick mark plus the stacked lines of text.
  const axisHeight = Math.round(fontSize * (labelLines * 1.2) + 6);

  return { interval, fontSize, axisHeight };
}

/**
 * Custom XAxis tick that stacks the label on separate lines below the axis
 * line — "dd/mm/yy" becomes Dd / Mm / yy, "Mar/25" becomes Mar / 25. Splits
 * on '/', so it handles both weekly and monthly labels without change.
 *
 * A fixed stacked block is narrow and predictable regardless of how many
 * labels are showing, so nothing runs off the page.
 *
 * Pass this as <XAxis tick={makeStackedTick(fontSize)} />
 */
function makeStackedTick(fontSize) {
  const lineGap = fontSize * 1.15;
  return function StackedTick(props) {
    const { x, y, payload } = props;
    const parts = String(payload.value).split('/'); // [dd, mm, yy] or [Mmm, yy]

    return (
      <g>
        <line x1={x} y1={y} x2={x} y2={y + 4} stroke="#d1d5db" strokeWidth={1} />
        {parts.map((part, i) => (
          <text
            key={i}
            x={x}
            y={y + 13 + i * lineGap}
            textAnchor="middle"
            fill="#6b7280"
            fontSize={fontSize}
            fontFamily="sans-serif"
          >
            {part}
          </text>
        ))}
      </g>
    );
  };
}

function donutWedgePDF(cx, cy, outerR, innerR, startDeg, endDeg) {
  const toRad = (deg) => (deg - 180) * (Math.PI / 180);
  const ox1 = cx + outerR * Math.cos(toRad(startDeg));
  const oy1 = cy + outerR * Math.sin(toRad(startDeg));
  const ox2 = cx + outerR * Math.cos(toRad(endDeg));
  const oy2 = cy + outerR * Math.sin(toRad(endDeg));
  const ix1 = cx + innerR * Math.cos(toRad(endDeg));
  const iy1 = cy + innerR * Math.sin(toRad(endDeg));
  const ix2 = cx + innerR * Math.cos(toRad(startDeg));
  const iy2 = cy + innerR * Math.sin(toRad(startDeg));
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${ox1} ${oy1}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${ox2} ${oy2}`,
    `L ${ix1} ${iy1}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${ix2} ${iy2}`,
    'Z',
  ].join(' ');
}

function SpeedDialPDF({ value, title, type }) {
  const maxValue = 5;
  const clampedValue = Math.min(Math.max(value, 0), maxValue);
  const needleAngle = (clampedValue / maxValue) * 180 - 90;

  const getColor = () => {
    if (type === 'happiness') {
      if (clampedValue >= 3.0) return COLORS.green;
      if (clampedValue >= 1.5) return COLORS.yellow;
      return COLORS.red;
    } else {
      if (clampedValue <= 2.5) return COLORS.green;
      if (clampedValue <= 3.5) return COLORS.yellow;
      return COLORS.red;
    }
  };

  const color = getColor();
  const cx = 100, cy = 105;
  const outerR = 78, innerR = 58;

  const zones = type === 'happiness'
    ? [
        { color: COLORS.red,    start: 0,   end: 54  },
        { color: COLORS.yellow, start: 54,  end: 108 },
        { color: COLORS.green,  start: 108, end: 180 },
      ]
    : [
        { color: COLORS.green,  start: 0,   end: 90  },
        { color: COLORS.yellow, start: 90,  end: 126 },
        { color: COLORS.red,    start: 126, end: 180 },
      ];

  return (
    <div className="speed-dial-pdf">
      <div className="speed-dial-title-pdf">{title}</div>
      <svg viewBox="0 0 200 125" className="gauge-svg-pdf">
        {zones.map((z, i) => (
          <path key={i} d={donutWedgePDF(cx, cy, outerR, innerR, z.start, z.end)} fill={z.color} opacity="0.25" />
        ))}
        <g transform={`rotate(${needleAngle}, ${cx}, ${cy})`}>
          <line x1={cx} y1={cy} x2={cx} y2={cy - innerR - 5} stroke={color} strokeWidth="3" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="7" fill={color} />
          <circle cx={cx} cy={cy} r="3.5" fill="#ffffff" />
        </g>
        <text x="16"  y="118" fontSize="11" fill="#9ca3af" fontWeight="500" textAnchor="middle">0</text>
        <text x="100" y="20"  fontSize="11" fill="#9ca3af" fontWeight="500" textAnchor="middle">2.5</text>
        <text x="184" y="118" fontSize="11" fill="#9ca3af" fontWeight="500" textAnchor="middle">5</text>
      </svg>
      <div className="speed-dial-value-pdf" style={{ color }}>{value.toFixed(2)}</div>
    </div>
  );
}

function ReportsSection({ teamAverages = [], teams = [], companyData = {}, approvedEmployees = [], billingOverLimit = false }) {
  const [reportTitle, setReportTitle] = useState('');
  const [timePeriod, setTimePeriod] = useState('26weeks');
  const [dataGranularity, setDataGranularity] = useState('week');
  const [pageOrientation, setPageOrientation] = useState('portrait');
  const [includeTeamCharts, setIncludeTeamCharts] = useState(true);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [renderCharts, setRenderCharts] = useState(false);

  const companyChartRef   = useRef(null);
  const barChartRef       = useRef(null);
  const engagementRef     = useRef(null);
  const teamChartRefs     = useRef({});

  const MAX_TEAMS_IN_BAR_CHART = 15;

  const twoWeekKeys      = useMemo(() => [getMondayOfWeek(1), getMondayOfWeek(2)], []);
  const previousTwoWeekKeys = useMemo(() => [getMondayOfWeek(3), getMondayOfWeek(4)], []);

  // Monthly grouping is offered only for 26 weeks / 52 weeks / all time.
  const monthlyAvailable = useMemo(() => {
    const period = TIME_PERIODS.find(p => p.value === timePeriod);
    if (!period) return false;
    return period.weeks === null || period.weeks >= MIN_WEEKS_FOR_MONTHLY;
  }, [timePeriod]);

  // Effective granularity — falls back to weekly on short periods even if the
  // user previously picked monthly.
  const isMonthly = monthlyAvailable && dataGranularity === 'month';
  const bucketLines = isMonthly ? 2 : 3;

  const bucketOf   = (weekKey) => (isMonthly ? getMonthKey(weekKey) : weekKey);
  const labelOf    = (key)     => (isMonthly ? formatMonthLabel(key) : formatWeekLabel(key));

  const allWeekKeys = useMemo(() => {
    const keys = new Set(teamAverages.map(getWeekKey).filter(Boolean));
    return [...keys].sort();
  }, [teamAverages]);

  const filteredWeekKeys = useMemo(() => {
    const period = TIME_PERIODS.find(p => p.value === timePeriod);
    if (!period || period.weeks === null) return allWeekKeys;
    const cutoff = getMondayOfWeek(period.weeks);
    return allWeekKeys.filter(k => k >= cutoff);
  }, [timePeriod, allWeekKeys]);

  const last26WeekKeys = useMemo(() => {
    const cutoff = getMondayOfWeek(26);
    return allWeekKeys.filter(k => k >= cutoff);
  }, [allWeekKeys]);

  const periodLabel = useMemo(() => {
    const base = TIME_PERIODS.find(p => p.value === timePeriod)?.label ?? '';
    return isMonthly ? base + ' (by month)' : base;
  }, [timePeriod, isMonthly]);

  const granularityWord = isMonthly ? 'Monthly' : 'Weekly';

  const companyLineData = useMemo(() => {
    const bucketMap = {};
    teamAverages
      .filter(ta => filteredWeekKeys.includes(getWeekKey(ta)))
      .forEach(ta => {
        const key = bucketOf(getWeekKey(ta));
        if (!bucketMap[key]) bucketMap[key] = { happiness: 0, tiredness: 0, total: 0 };
        const n = ta.surveyCount || 0;
        bucketMap[key].happiness += ta.avgHappiness * n;
        bucketMap[key].tiredness += ta.avgTirednessOverall * n;
        bucketMap[key].total += n;
      });

    return Object.entries(bucketMap)
      .filter(([, d]) => d.total > 0)
      .map(([key, d]) => ({
        dateKey: key,
        date: labelOf(key),
        happiness: +(d.happiness / d.total).toFixed(2),
        tiredness: +(d.tiredness / d.total).toFixed(2),
      }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [teamAverages, filteredWeekKeys, isMonthly]);

  const periodTotalResponses = useMemo(() => {
    return teamAverages
      .filter(ta => filteredWeekKeys.includes(getWeekKey(ta)))
      .reduce((a, b) => a + (b.surveyCount || 0), 0);
  }, [teamAverages, filteredWeekKeys]);

  const weeklyResponseData = useMemo(() => {
    const bucketMap = {};
    teamAverages
      .filter(ta => last26WeekKeys.includes(getWeekKey(ta)))
      .forEach(ta => {
        const key = bucketOf(getWeekKey(ta));
        if (!bucketMap[key]) bucketMap[key] = 0;
        bucketMap[key] += ta.surveyCount || 0;
      });
    return Object.entries(bucketMap)
      .map(([key, count]) => ({ dateKey: key, date: labelOf(key), responses: count }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [teamAverages, last26WeekKeys, isMonthly]);

  // Count of actual weeks with data — independent of chart grouping, so
  // "Avg per Week" stays a per-week figure in monthly mode.
  const weeksWithData = useMemo(() => {
    const keys = new Set(
      teamAverages
        .filter(ta => last26WeekKeys.includes(getWeekKey(ta)) && (ta.surveyCount || 0) > 0)
        .map(getWeekKey)
    );
    return keys.size;
  }, [teamAverages, last26WeekKeys]);

  const engagementStats = useMemo(() => {
    const totalResponses = weeklyResponseData.reduce((a, b) => a + b.responses, 0);
    const avgPerWeek = weeksWithData > 0 ? Math.round(totalResponses / weeksWithData) : 0;
    const totalEmployees = approvedEmployees?.length ?? 0;
    const responsesPerEmployee = totalEmployees > 0
      ? (totalResponses / totalEmployees).toFixed(1)
      : '0';
    return { totalResponses, avgPerWeek, responsesPerEmployee, weeks: weeksWithData };
  }, [weeklyResponseData, approvedEmployees, weeksWithData]);

  const teamBarData = useMemo(() => {
    const stats = {};
    teamAverages
      .filter(ta => twoWeekKeys.includes(getWeekKey(ta)))
      .forEach(ta => {
        if (!stats[ta.teamId]) stats[ta.teamId] = { happiness: 0, tiredness: 0, total: 0 };
        const n = ta.surveyCount || 0;
        stats[ta.teamId].happiness += ta.avgHappiness * n;
        stats[ta.teamId].tiredness += ta.avgTirednessOverall * n;
        stats[ta.teamId].total += n;
      });

    return teams
      .filter(t => stats[t.id] && stats[t.id].total > 0)
      .map(t => ({
        id: t.id, name: t.name, fullName: t.name,
        happiness: +(stats[t.id].happiness / stats[t.id].total).toFixed(2),
        tiredness: +(stats[t.id].tiredness / stats[t.id].total).toFixed(2),
        responseCount: stats[t.id].total,
      }))
      .sort((a, b) => b.responseCount - a.responseCount)
      .slice(0, MAX_TEAMS_IN_BAR_CHART);
  }, [teamAverages, teams, twoWeekKeys]);

  const isShowingLimitedTeams = useMemo(() => {
    const teamsWithData = teams.filter(t =>
      teamAverages.some(ta => ta.teamId === t.id && twoWeekKeys.includes(getWeekKey(ta)))
    );
    return teamsWithData.length > MAX_TEAMS_IN_BAR_CHART;
  }, [teamAverages, teams, twoWeekKeys]);

  // Leaderboard covers the last 2 completed weeks. Movement arrows compare
  // that against the 2 completed weeks immediately before it.
  const teamLeaderboard = useMemo(() => {
    if (!approvedEmployees || !Array.isArray(approvedEmployees)) return [];

    const buildRanking = (weekKeys) => {
      const stats = teams.map(team => {
        const memberCount = approvedEmployees.filter(emp => emp.teamId === team.id).length;
        const responseCount = teamAverages
          .filter(ta => ta.teamId === team.id && weekKeys.includes(getWeekKey(ta)))
          .reduce((a, b) => a + (b.surveyCount || 0), 0);
        const responsesPerPerson = memberCount > 0 ? responseCount / memberCount : 0;
        return { id: team.id, responsesPerPerson };
      });
      return stats
        .filter(t => t.responsesPerPerson > 0)
        .sort((a, b) => b.responsesPerPerson - a.responsesPerPerson)
        .reduce((acc, team, index) => { acc[team.id] = index + 1; return acc; }, {});
    };

    const previousRanked = buildRanking(previousTwoWeekKeys);

    const currentStats = teams.map(team => {
      const memberCount = approvedEmployees.filter(emp => emp.teamId === team.id).length;
      const responseCount = teamAverages
        .filter(ta => ta.teamId === team.id && twoWeekKeys.includes(getWeekKey(ta)))
        .reduce((a, b) => a + (b.surveyCount || 0), 0);
      const responsesPerPerson = memberCount > 0 ? responseCount / memberCount : 0;
      return { id: team.id, name: team.name, responsesPerPerson, memberCount, responseCount };
    });

    return currentStats
      .filter(t => t.responsesPerPerson > 0)
      .sort((a, b) =>
        b.responsesPerPerson !== a.responsesPerPerson
          ? b.responsesPerPerson - a.responsesPerPerson
          : b.responseCount - a.responseCount
      )
      .map((team, index) => {
        const currentRank = index + 1;
        const previousRank = previousRanked[team.id];
        const rankChange = previousRank !== undefined ? previousRank - currentRank : null;
        return { ...team, rank: currentRank, rankChange };
      });
  }, [teamAverages, teams, approvedEmployees, twoWeekKeys, previousTwoWeekKeys]);

  // Bucket keys spanning the selected period — weeks, or the calendar months
  // those weeks fall in. Used as the x-axis domain for each team chart so all
  // team charts line up.
  const periodBucketKeys = useMemo(() => {
    if (!isMonthly) return filteredWeekKeys;
    return [...new Set(filteredWeekKeys.map(getMonthKey))].sort();
  }, [filteredWeekKeys, isMonthly]);

  const individualTeamData = useMemo(() => {
    if (!includeTeamCharts) return [];
    return selectedTeams.map(teamId => {
      const team = teams.find(t => t.id === teamId);
      if (!team) return null;
      const teamTAs = teamAverages.filter(ta => ta.teamId === teamId);
      if (teamTAs.length === 0) return null;

      const recentTAs = teamTAs.filter(ta => twoWeekKeys.includes(getWeekKey(ta)));
      const recentTotal = recentTAs.reduce((a, b) => a + (b.surveyCount || 0), 0);
      const avgHappiness = recentTotal > 0
        ? recentTAs.reduce((a, b) => a + b.avgHappiness * (b.surveyCount || 0), 0) / recentTotal : 0;
      const avgTiredness = recentTotal > 0
        ? recentTAs.reduce((a, b) => a + b.avgTirednessOverall * (b.surveyCount || 0), 0) / recentTotal : 0;

      // Response-weighted average per bucket. rawCount/rawH/rawT are a fallback
      // for buckets whose records all have surveyCount 0.
      const acc = {};
      teamTAs.forEach(ta => {
        const wk = getWeekKey(ta);
        if (!filteredWeekKeys.includes(wk)) return;
        const key = bucketOf(wk);
        if (!acc[key]) acc[key] = { h: 0, t: 0, n: 0, rawH: 0, rawT: 0, rawCount: 0 };
        const n = ta.surveyCount || 0;
        acc[key].h += ta.avgHappiness * n;
        acc[key].t += ta.avgTirednessOverall * n;
        acc[key].n += n;
        acc[key].rawH += ta.avgHappiness;
        acc[key].rawT += ta.avgTirednessOverall;
        acc[key].rawCount += 1;
      });

      const lineData = periodBucketKeys.map(key => {
        const d = acc[key];
        let happiness = null, tiredness = null;
        if (d && d.n > 0) {
          happiness = +(d.h / d.n).toFixed(2);
          tiredness = +(d.t / d.n).toFixed(2);
        } else if (d && d.rawCount > 0) {
          happiness = +(d.rawH / d.rawCount).toFixed(2);
          tiredness = +(d.rawT / d.rawCount).toFixed(2);
        }
        return { dateKey: key, date: labelOf(key), happiness, tiredness };
      });

      return {
        teamId, teamName: team.name, avgHappiness, avgTiredness, lineData,
        responses: teamTAs.reduce((a, b) => a + (b.surveyCount || 0), 0),
      };
    }).filter(Boolean);
  }, [teamAverages, teams, selectedTeams, includeTeamCharts, twoWeekKeys, filteredWeekKeys, periodBucketKeys, isMonthly]);

  const toggleTeam = (teamId) => setSelectedTeams(prev =>
    prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
  );

  const hasData = companyLineData.length > 0;
  const totalResponsesInPeriod = periodTotalResponses;
  const yAxisTicksInteger = [0, 1, 2, 3, 4, 5];
  const isLandscape = pageOrientation === 'landscape';

  const getReportSummary = () => {
    const items = [
      'Page layout: ' + (isLandscape ? 'Landscape' : 'Portrait'),
      'Data grouping: ' + (isMonthly ? 'By month' : 'By week'),
      'Company overview ' + granularityWord.toLowerCase() + ' line chart (' + periodLabel + ')',
      'Team averages bar chart (last 2 completed weeks)' + (isShowingLimitedTeams ? ': top 15 teams' : ''),
      'Engagement statistics & response graph (last 26 weeks)',
      'Company speed dials — happiness & tiredness',
      'Team engagement leaderboard (last 2 completed weeks)',
    ];
    if (includeTeamCharts && selectedTeams.length > 0) {
      items.push(`Individual charts for ${selectedTeams.length} team${selectedTeams.length !== 1 ? 's' : ''}`);
    }
    return items;
  };

  const captureChart = async (ref, width, height) => {
    if (!ref?.current) return null;
    const canvas = await html2canvas(ref.current, {
      scale: 2, backgroundColor: '#ffffff', logging: false,
      width: width || 800, height: height || 200,
    });
    return canvas.toDataURL('image/jpeg', 0.75);
  };

  const loadLogoBase64 = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/Zella logo white.png';
    });
  };

  const generatePDF = async () => {
    if (!canGenerate || billingOverLimit) return;
    setIsGenerating(true);
    setRenderCharts(true);
    await new Promise(r => setTimeout(r, 1500));

    try {
      const pdf = new jsPDF(isLandscape ? 'l' : 'p', 'mm', 'a4');
      const pageWidth  = isLandscape ? 297 : 210;
      const pageHeight = isLandscape ? 210 : 297;
      const margin     = 10;
      const contentWidth = pageWidth - (margin * 2);
      const footerY    = pageHeight - 8;

      const logoBase64 = await loadLogoBase64();

      const drawHeader = () => {
        pdf.setFillColor(3, 32, 84);
        pdf.rect(0, 0, pageWidth, 36, 'F');
        pdf.setFillColor(2, 20, 55);
        pdf.rect(0, 0, pageWidth, 2, 'F');
        pdf.setFillColor(26, 176, 42);
        pdf.triangle(pageWidth - 70, 0, pageWidth, 0, pageWidth, 36);
        pdf.setFillColor(3, 32, 84);
        pdf.triangle(pageWidth - 45, 0, pageWidth, 0, pageWidth, 24);
        pdf.setFillColor(45, 200, 60);
        pdf.triangle(pageWidth - 22, 0, pageWidth, 0, pageWidth, 11);

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text(reportTitle || 'Wellness Report', margin, 12);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(200, 220, 255);
        pdf.text(companyData?.name || '', margin, 20);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 180, 230);
        pdf.text(periodLabel, margin, 28);

        if (logoBase64) {
          pdf.addImage(logoBase64, 'PNG', pageWidth - margin - 36, 4, 28, 28);
        } else {
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Zella', pageWidth - margin - 20, 18);
        }

        const statsBarY = 38;
        const statsBarH = 7;
        pdf.setFillColor(249, 250, 251);
        pdf.roundedRect(margin, statsBarY, contentWidth, statsBarH, 2, 2, 'F');
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(margin, statsBarY, contentWidth, statsBarH, 2, 2, 'S');

        const statsTextY = statsBarY + (statsBarH / 2) + 1.2;
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.text(totalResponsesInPeriod.toLocaleString() + ' responses', margin + 4, statsTextY);
        pdf.text(teams.length + ' teams', margin + 30, statsTextY);

        const legendX = pageWidth - margin - 55;
        pdf.setFillColor(26, 176, 42);
        pdf.circle(legendX, statsTextY - 1, 1.95, 'F');
        pdf.setTextColor(55, 65, 81);
        pdf.setFontSize(7);
        pdf.text('Happiness', legendX + 4, statsTextY);
        pdf.setFillColor(255, 164, 5);
        pdf.circle(legendX + 30, statsTextY - 1, 1.95, 'F');
        pdf.text('Tiredness', legendX + 34, statsTextY);

        return 52;
      };

      const drawHeaderLandscape = () => {
        const headerH = 20;
        pdf.setFillColor(3, 32, 84);
        pdf.rect(0, 0, pageWidth, headerH, 'F');
        pdf.setFillColor(2, 20, 55);
        pdf.rect(0, 0, pageWidth, 1.2, 'F');
        pdf.setFillColor(26, 176, 42);
        pdf.triangle(pageWidth - 50, 0, pageWidth, 0, pageWidth, headerH);
        pdf.setFillColor(3, 32, 84);
        pdf.triangle(pageWidth - 32, 0, pageWidth, 0, pageWidth, headerH * 0.65);
        pdf.setFillColor(45, 200, 60);
        pdf.triangle(pageWidth - 16, 0, pageWidth, 0, pageWidth, headerH * 0.3);

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(reportTitle || 'Wellness Report', margin, 8);
        pdf.setFontSize(7.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(200, 220, 255);
        const subtitle = [companyData?.name, periodLabel].filter(Boolean).join('   •   ');
        pdf.text(subtitle, margin, 14);
        pdf.setFontSize(6.5);
        pdf.setTextColor(180, 200, 240);
        const statsText = totalResponsesInPeriod.toLocaleString() + ' responses   •   ' + teams.length + ' teams';
        pdf.text(statsText, margin, 18);

        // Happiness / Tiredness key — same key as the portrait stats bar, kept
        // inside the navy header so it costs no vertical space. Sits clear of
        // the green corner triangles at this baseline.
        const legendY = 18;
        const legendX = pageWidth - margin - 62;
        pdf.setFillColor(26, 176, 42);
        pdf.circle(legendX, legendY - 1, 1.5, 'F');
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(235, 243, 255);
        pdf.text('Happiness', legendX + 3, legendY);
        pdf.setFillColor(255, 164, 5);
        pdf.circle(legendX + 26, legendY - 1, 1.5, 'F');
        pdf.text('Tiredness', legendX + 29, legendY);

        if (logoBase64) {
          pdf.addImage(logoBase64, 'PNG', pageWidth - margin - 18, 2, 16, 16);
        } else {
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Zella', pageWidth - margin - 16, 12);
        }

        return headerH + 4;
      };

      const drawSectionTitle = (title, y, small = false) => {
        pdf.setTextColor(3, 32, 84);
        pdf.setFontSize(small ? 8.5 : 10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(title, margin, y);
        return y + (small ? 3 : 4);
      };

      const drawFooters = () => {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(156, 163, 175);
          pdf.text('Generated ' + new Date().toLocaleDateString('en-GB'), margin, footerY);
          pdf.text('Page ' + i + ' of ' + totalPages, pageWidth - margin - 18, footerY);
        }
      };

      const companyChartTitle  = 'Company Overview (' + granularityWord + ')';
      const responseChartTitle = granularityWord + ' Response Volume';

      let y;

      if (!isLandscape) {
        y = drawHeader();
        y = drawSectionTitle(companyChartTitle, y);
        if (companyLineData.length > 0) {
          const img = await captureChart(companyChartRef, 850, 310);
          if (img) { pdf.addImage(img, 'JPEG', margin, y, contentWidth, 72); y += 67; }
        }

        const barChartTitle = isShowingLimitedTeams
          ? 'Team Averages: Top 15 Teams (Last 2 Completed Weeks)'
          : 'Team Averages (Last 2 Completed Weeks)';
        y = drawSectionTitle(barChartTitle, y);
        if (teamBarData.length > 0) {
          const img = await captureChart(barChartRef, 850, 350);
          if (img) { pdf.addImage(img, 'JPEG', margin, y, contentWidth, 84); y += 79; }
        }

        y = drawSectionTitle('Engagement: Last 26 Weeks', y);
        y += 1;

        const statBoxW = (contentWidth - 2 * 4) / 3;
        const statBoxH = 13;
        const statBoxes = [
          { label: 'Total Responses',          value: engagementStats.totalResponses.toLocaleString() },
          { label: 'Avg per Week',             value: engagementStats.avgPerWeek.toLocaleString() },
          { label: 'Responses per Employee',   value: String(engagementStats.responsesPerEmployee) },
        ];

        statBoxes.forEach((box, i) => {
          const bx = margin + i * (statBoxW + 4);
          pdf.setFillColor(249, 250, 251);
          pdf.setDrawColor(229, 231, 235);
          pdf.setLineWidth(0.3);
          pdf.roundedRect(bx, y, statBoxW, statBoxH, 2, 2, 'FD');
          pdf.setTextColor(107, 114, 128);
          pdf.setFontSize(6);
          pdf.setFont('helvetica', 'normal');
          pdf.text(box.label, bx + statBoxW / 2, y + 4.5, { align: 'center' });
          pdf.setTextColor(3, 32, 84);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text(box.value, bx + statBoxW / 2, y + 10, { align: 'center' });
        });

        y += statBoxH + 10;
        y = drawSectionTitle(responseChartTitle, y, true);
        if (weeklyResponseData.length > 0) {
          const img = await captureChart(engagementRef, 850, 200);
          if (img) {
            const chartH = Math.min(45, footerY - y - 5);
            pdf.addImage(img, 'JPEG', margin, y, contentWidth, chartH);
            y += chartH + 3;
          }
        }

        if (includeTeamCharts && individualTeamData.length > 0) {
          pdf.addPage();
          y = margin;
          const teamsPerPage = 3;
          for (let i = 0; i < individualTeamData.length; i++) {
            if (i > 0 && i % teamsPerPage === 0) { pdf.addPage(); y = margin; }
            const teamData = individualTeamData[i];
            pdf.setFillColor(3, 32, 84);
            pdf.roundedRect(margin, y, 4, 10, 1.5, 1.5, 'F');
            pdf.setTextColor(31, 41, 55);
            pdf.setFontSize(13);
            pdf.setFont('helvetica', 'bold');
            pdf.text(teamData.teamName, margin + 7, y + 7);
            y += 14;
            const ref = teamChartRefs.current[teamData.teamId];
            if (ref) {
              const img = await captureChart({ current: ref }, 850, 300);
              if (img) { pdf.addImage(img, 'JPEG', margin, y, contentWidth, 72); y += 76; }
            }
          }
        }

      } else {
        y = drawHeaderLandscape();
        y = drawSectionTitle(companyChartTitle, y, true);
        if (companyLineData.length > 0) {
          const img = await captureChart(companyChartRef, 1400, 290);
          if (img) { pdf.addImage(img, 'JPEG', margin, y, contentWidth, 56); y += 56; }
        }
        y += 3;

        const barChartTitle = isShowingLimitedTeams
          ? 'Team Averages: Top 15 Teams (Last 2 Completed Weeks)'
          : 'Team Averages (Last 2 Completed Weeks)';
        y = drawSectionTitle(barChartTitle, y, true);
        if (teamBarData.length > 0) {
          const img = await captureChart(barChartRef, 1400, 300);
          if (img) { pdf.addImage(img, 'JPEG', margin, y, contentWidth, 60); y += 60; }
        }
        y += 3;

        y = drawSectionTitle('Engagement: Last 26 Weeks', y, true);
        y += 1;

        const statBoxW = (contentWidth - 2 * 4) / 3;
        const statBoxH = 10;
        const statBoxes = [
          { label: 'Total Responses',          value: engagementStats.totalResponses.toLocaleString() },
          { label: 'Avg per Week',             value: engagementStats.avgPerWeek.toLocaleString() },
          { label: 'Responses per Employee',   value: String(engagementStats.responsesPerEmployee) },
        ];

        statBoxes.forEach((box, i) => {
          const bx = margin + i * (statBoxW + 4);
          pdf.setFillColor(249, 250, 251);
          pdf.setDrawColor(229, 231, 235);
          pdf.setLineWidth(0.3);
          pdf.roundedRect(bx, y, statBoxW, statBoxH, 2, 2, 'FD');
          pdf.setTextColor(107, 114, 128);
          pdf.setFontSize(5.5);
          pdf.setFont('helvetica', 'normal');
          pdf.text(box.label, bx + statBoxW / 2, y + 4, { align: 'center' });
          pdf.setTextColor(3, 32, 84);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text(box.value, bx + statBoxW / 2, y + 8.5, { align: 'center' });
        });

        y += statBoxH + 5;
        y = drawSectionTitle(responseChartTitle, y, true);
        if (weeklyResponseData.length > 0) {
          const img = await captureChart(engagementRef, 1400, 182);
          if (img) {
            const chartH = Math.min(36, footerY - y - 4);
            pdf.addImage(img, 'JPEG', margin, y, contentWidth, chartH);
            y += chartH;
          }
        }

        if (includeTeamCharts && individualTeamData.length > 0) {
          pdf.addPage();
          y = margin;
          const teamsPerPage = 2;
          for (let i = 0; i < individualTeamData.length; i++) {
            if (i > 0 && i % teamsPerPage === 0) { pdf.addPage(); y = margin; }
            const teamData = individualTeamData[i];
            pdf.setFillColor(3, 32, 84);
            pdf.roundedRect(margin, y, 4, 10, 1.5, 1.5, 'F');
            pdf.setTextColor(31, 41, 55);
            pdf.setFontSize(13);
            pdf.setFont('helvetica', 'bold');
            pdf.text(teamData.teamName, margin + 7, y + 7);
            y += 14;
            const ref = teamChartRefs.current[teamData.teamId];
            if (ref) {
              const img = await captureChart({ current: ref }, 1400, 384);
              if (img) { pdf.addImage(img, 'JPEG', margin, y, contentWidth, 76); y += 80; }
            }
          }
        }
      }

      pdf.addPage();
      y = margin;
      pdf.setFillColor(3, 32, 84);
      pdf.rect(0, 0, pageWidth, 16, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Team Engagement Leaderboard: Last 2 Completed Weeks', margin, 7.5);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(180, 200, 240);
      pdf.text('Ranked by survey responses per team member. Arrow up/down shows movement vs the 2 weeks before that.', margin, 13);
      y = 22;

      const teamCount = teamLeaderboard.length;
      let leaderCols, rowH, fontSize, rankFontSize;
      if (teamCount <= 5) {
        leaderCols = 1; rowH = 10; fontSize = 8; rankFontSize = 7.5;
      } else if (teamCount <= 15) {
        leaderCols = 1; rowH = 7; fontSize = 7; rankFontSize = 6.5;
      } else if (teamCount <= 25) {
        leaderCols = 2; rowH = 5.5; fontSize = 6.5; rankFontSize = 6;
      } else {
        leaderCols = 3; rowH = 4.2; fontSize = 5.5; rankFontSize = 5;
      }

      const colGap = 4;
      const colW = (contentWidth - (leaderCols - 1) * colGap) / leaderCols;
      const shownTeams = Math.min(teamLeaderboard.length, 75);
      const teamsPerCol = Math.ceil(teamLeaderboard.length / leaderCols);

      for (let i = 0; i < shownTeams; i++) {
        const team = teamLeaderboard[i];
        const col  = Math.floor(i / teamsPerCol);
        const row  = i % teamsPerCol;
        const itemX = margin + col * (colW + colGap);
        const itemY = y + row * (rowH + 1);

        if (team.rank === 1)      pdf.setFillColor(254, 243, 199);
        else if (team.rank === 2) pdf.setFillColor(229, 231, 235);
        else if (team.rank === 3) pdf.setFillColor(255, 237, 213);
        else                       pdf.setFillColor(248, 249, 250);
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.15);
        pdf.roundedRect(itemX, itemY, colW, rowH - 0.5, 1.2, 1.2, 'FD');

        const textY = itemY + rowH * 0.62;
        const rankW = teamCount <= 15 ? 10 : 8;

        pdf.setFontSize(rankFontSize);
        if (team.rank === 1)      { pdf.setTextColor(202, 138, 4);   pdf.setFont('helvetica', 'bold'); }
        else if (team.rank === 2) { pdf.setTextColor(107, 114, 128); pdf.setFont('helvetica', 'bold'); }
        else if (team.rank === 3) { pdf.setTextColor(180, 83, 9);    pdf.setFont('helvetica', 'bold'); }
        else                       { pdf.setTextColor(156, 163, 175); pdf.setFont('helvetica', 'normal'); }
        const rankStr = team.rank <= 3 ? ['1st', '2nd', '3rd'][team.rank - 1] : '#' + team.rank;
        pdf.text(rankStr, itemX + 1.5, textY);

        const hasPrior = team.rankChange !== null;
        const isUp   = hasPrior && team.rankChange > 0;
        const isDown = hasPrior && team.rankChange < 0;
        const movColor = !hasPrior || team.rankChange === 0
          ? [156, 163, 175]
          : isUp ? [16, 185, 129] : [239, 68, 68];

        const triSize = Math.max(1.4, rowH * 0.28);
        const triX = itemX + colW - triSize - 1.5;
        const triCY = itemY + rowH * 0.5;

        pdf.setFillColor(...movColor);
        pdf.setDrawColor(...movColor);

        if (isUp) {
          pdf.triangle(triX, triCY + triSize * 0.5, triX + triSize, triCY + triSize * 0.5, triX + triSize * 0.5, triCY - triSize * 0.5, 'F');
        } else if (isDown) {
          pdf.triangle(triX, triCY - triSize * 0.5, triX + triSize, triCY - triSize * 0.5, triX + triSize * 0.5, triCY + triSize * 0.5, 'F');
        } else {
          const eqW = triSize;
          const eqH = Math.max(0.4, triSize * 0.18);
          const eqX = triX;
          pdf.setFillColor(...movColor);
          pdf.rect(eqX, triCY - triSize * 0.2, eqW, eqH, 'F');
          pdf.rect(eqX, triCY + triSize * 0.1, eqW, eqH, 'F');
        }

        const movW = triSize + 1.5;
        pdf.setFont('helvetica', team.rank <= 3 ? 'bold' : 'normal');
        pdf.setTextColor(55, 65, 81);
        pdf.setFontSize(fontSize);
        const nameMaxW = colW - rankW - movW - 6;
        let displayName = team.name;
        while (pdf.getTextWidth(displayName) > nameMaxW && displayName.length > 3) {
          displayName = displayName.slice(0, -2) + '…';
        }
        pdf.text(displayName, itemX + rankW, textY);
      }

      drawFooters();

      const filename = (reportTitle || 'Report').replace(/\s+/g, '_') + '_' + new Date().toISOString().split('T')[0] + '.pdf';
      pdf.save(filename);

    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
      setTimeout(() => setRenderCharts(false), 300);
    }
  };

  const canGenerate = hasData;

  // ─── Chart components ────────────────────────────────────────────────────────
  // All three use getXAxisConfig() so font size and interval scale with data
  // density, and makeStackedTick() for the stacked label block — Dd/Mm/yy when
  // grouping by week, Mmm/yy when grouping by month.

  const CompanyLineChart = ({ data, height = 252 }) => {
    const { interval, fontSize, axisHeight } = getXAxisConfig(data.length, isLandscape, false, true, bucketLines);
    const stackedTick = makeStackedTick(fontSize);
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 15, bottom: axisHeight, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tick={stackedTick}
            height={axisHeight}
            interval={interval}
            tickLine={false}
            axisLine={{ stroke: '#d1d5db' }}
          />
          <YAxis domain={[0, 5]} ticks={yAxisTicksInteger} tick={{ fontSize: 11, fill: '#6b7280' }} width={28} tickFormatter={formatYTick} tickLine={{ stroke: '#d1d5db' }} axisLine={{ stroke: '#d1d5db' }} />
          <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '11px' }} />
          <Line type="monotone" dataKey="happiness" stroke={COLORS.happiness} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} connectNulls />
          <Line type="monotone" dataKey="tiredness" stroke={COLORS.tiredness} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const EngagementLineChart = ({ data, height = 185 }) => {
    // Engagement chart always covers 26 weeks (fixed range), grouped by the
    // selected granularity.
    const { interval, fontSize, axisHeight } = getXAxisConfig(data.length, isLandscape, false, false, bucketLines);
    const stackedTick = makeStackedTick(fontSize);
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 15, bottom: axisHeight, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tick={stackedTick}
            height={axisHeight}
            interval={interval}
            tickLine={false}
            axisLine={{ stroke: '#d1d5db' }}
          />
          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} width={32} tickLine={{ stroke: '#d1d5db' }} axisLine={{ stroke: '#d1d5db' }} />
          <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '11px' }} formatter={(v) => [v, 'Responses']} />
          <Line type="monotone" dataKey="responses" stroke="#032054" strokeWidth={2.5} dot={{ r: 3, fill: '#032054', strokeWidth: 0 }} isAnimationActive={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const TeamLineChart = ({ data, height = 250 }) => {
    const { interval, fontSize, axisHeight } = getXAxisConfig(data.length, isLandscape, true, false, bucketLines);
    const stackedTick = makeStackedTick(fontSize);
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 10, bottom: axisHeight, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tick={stackedTick}
            height={axisHeight}
            interval={interval}
            tickLine={false}
            axisLine={{ stroke: '#d1d5db' }}
          />
          <YAxis domain={[0, 5]} ticks={[0,1,2,3,4,5]} tickCount={6} tick={{ fontSize: 10, fill: '#6b7280' }} width={24} tickFormatter={formatYTick} tickLine={{ stroke: '#d1d5db' }} axisLine={{ stroke: '#d1d5db' }} />
          <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '10px' }} />
          <Line type="monotone" dataKey="happiness" stroke={COLORS.happiness} strokeWidth={1.5} dot={false} activeDot={{ r: 2 }} isAnimationActive={false} connectNulls={true} />
          <Line type="monotone" dataKey="tiredness" stroke={COLORS.tiredness} strokeWidth={1.5} dot={false} activeDot={{ r: 2 }} isAnimationActive={false} connectNulls={true} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <>
      <div className="dashboard-header">
        <div>
          <h1>Reports</h1>
          <p>Generate professional PDF wellness reports</p>
        </div>
      </div>

      <div className="reports-form">
        <section className="form-section">
          <label className="section-label">Report Title</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Q4 Wellness Report"
            value={reportTitle}
            onChange={(e) => setReportTitle(e.target.value)}
          />
        </section>

        <section className="form-section">
          <label className="section-label">Time Period for Overview Chart</label>
          <div className="radio-group">
            {TIME_PERIODS.map(period => (
              <label key={period.value} className={'radio-item ' + (timePeriod === period.value ? 'checked' : '')}>
                <input
                  type="radio"
                  name="timePeriod"
                  value={period.value}
                  checked={timePeriod === period.value}
                  onChange={(e) => setTimePeriod(e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-title">{period.label}</span>
                  <span className="radio-desc">
                    {period.value === '10weeks' && 'Short-term snapshot'}
                    {period.value === '26weeks' && '6-month view'}
                    {period.value === '52weeks' && 'Full year'}
                    {period.value === 'all'     && 'Complete historical data'}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </section>

        <section className="form-section">
          <label className="section-label">Chart Grouping</label>
          <div className="radio-group cols-2">
            {GRANULARITIES.map(g => {
              const disabled = g.value === 'month' && !monthlyAvailable;
              const checked = (isMonthly ? 'month' : 'week') === g.value;
              return (
                <label
                  key={g.value}
                  className={'radio-item ' + (checked ? 'checked ' : '') + (disabled ? 'disabled' : '')}
                >
                  <input
                    type="radio"
                    name="dataGranularity"
                    value={g.value}
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => setDataGranularity(e.target.value)}
                  />
                  <div className="radio-content">
                    <span className="radio-title">{g.label}</span>
                    <span className="radio-desc">
                      {disabled ? 'Needs a period of 26 weeks or more' : g.desc}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
          <p className="section-desc">
            {monthlyAvailable
              ? 'Applies to the company overview, response volume and team trend charts.'
              : 'Choose 26 weeks or longer to group the charts by month.'}
          </p>
        </section>

        <section className="form-section">
          <label className="section-label">Page Orientation</label>
          <div className="radio-group cols-2">
            {PAGE_ORIENTATIONS.map(orientation => (
              <label key={orientation.value} className={'radio-item ' + (pageOrientation === orientation.value ? 'checked' : '')}>
                <input
                  type="radio"
                  name="pageOrientation"
                  value={orientation.value}
                  checked={pageOrientation === orientation.value}
                  onChange={(e) => setPageOrientation(e.target.value)}
                />
                <div className="radio-content">
                  <span className="radio-title">{orientation.label}</span>
                  <span className="radio-desc">{orientation.desc}</span>
                </div>
              </label>
            ))}
          </div>
        </section>

        <section className="form-section">
          <div className="section-header">
            <label className="section-label">Individual Team Charts</label>
            <label className="toggle-container">
              <input type="checkbox" checked={includeTeamCharts} onChange={(e) => setIncludeTeamCharts(e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
          </div>
          <p className="section-desc">Include speedometer dashboards and trend charts for selected teams</p>
          {includeTeamCharts && (
            <>
              <div className="quick-actions">
                <button type="button" onClick={() => setSelectedTeams(teams.map(t => t.id))}>Select All</button>
                <button type="button" onClick={() => setSelectedTeams([])}>Clear</button>
              </div>
              <div className="team-buttons">
                {teams.map(team => (
                  <button key={team.id} type="button" className={'team-btn ' + (selectedTeams.includes(team.id) ? 'selected' : '')} onClick={() => toggleTeam(team.id)}>
                    {selectedTeams.includes(team.id) && <span className="check">✓</span>}
                    {team.name}
                  </button>
                ))}
              </div>
              {teams.length === 0 && <p className="empty-state">No teams available</p>}
            </>
          )}
        </section>

        <section className="form-section summary-section">
          <label className="section-label">Report Summary</label>
          <div className="summary-list">
            {getReportSummary().map((item, i) => (
              <div key={i} className="summary-item">
                <span className="summary-check">✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="summary-meta">
            <span>{totalResponsesInPeriod.toLocaleString()} responses in period</span>
            <span>{teams.length} teams</span>
            <span>{companyLineData.length} {isMonthly ? 'months' : 'weeks'} plotted</span>
            {isShowingLimitedTeams && <span className="summary-note">Bar chart limited to top 15 teams</span>}
          </div>
        </section>

        <section className="form-section generate-section">
          {billingOverLimit && (
            <div className="billing-gate-banner" style={{ marginBottom: '1rem' }}>
              <IconLock />
              <div>
                <strong>Reports unavailable</strong>
                <p>Your subscription limit has been reached or a payment has failed. Visit Payments &amp; Billing to restore access.</p>
              </div>
            </div>
          )}
          <button
            type="button"
            className={'generate-btn ' + (!canGenerate || isGenerating || billingOverLimit ? 'disabled' : '')}
            onClick={generatePDF}
            disabled={!canGenerate || isGenerating || billingOverLimit}
          >
            {isGenerating ? (<><span className="spinner"></span>Generating...</>) : 'Generate PDF'}
          </button>
          {!hasData && <p className="validation-msg">No survey data available</p>}
        </section>
      </div>

      {renderCharts && (
        <div className="hidden-charts" aria-hidden="true">
          {companyLineData.length > 0 && (
            <div
              ref={companyChartRef}
              className="chart-render-main"
              style={isLandscape ? { width: 1400, height: 290 } : { width: 850, height: 310 }}
            >
              <CompanyLineChart data={companyLineData} height={isLandscape ? 275 : 295} />
            </div>
          )}

          {teamBarData.length > 0 && (
            <div
              ref={barChartRef}
              className="chart-render-bar"
              style={isLandscape ? { width: 1400, height: 300 } : { width: 850, height: 350 }}
            >
              <ResponsiveContainer width="100%" height={isLandscape ? 280 : 330}>
                <BarChart data={teamBarData} margin={{ top: 10, right: 15, bottom: 90, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="fullName" tick={({ x, y, payload }) => {
                    const words = payload.value.split(' ');
                    return (
                      <g transform={`translate(${x},${y})`}>
                        {words.map((word, i) => (
                          <text key={i} x={0} y={i * 16} dy={14} textAnchor="middle" fill="#6b7280" fontSize={14}>{word}</text>
                        ))}
                      </g>
                    );
                  }} height={90} interval={0} tickLine={{ stroke: '#d1d5db' }} axisLine={{ stroke: '#d1d5db' }} />
                  <YAxis domain={[0, 5]} ticks={yAxisTicksInteger} tick={{ fontSize: 11, fill: '#6b7280' }} width={28} tickFormatter={formatYTick} tickLine={{ stroke: '#d1d5db' }} axisLine={{ stroke: '#d1d5db' }} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '10px' }} formatter={(v, n, p) => [v, p.payload.fullName || n]} />
                  <Bar dataKey="happiness" fill={COLORS.happiness} isAnimationActive={false} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="tiredness" fill={COLORS.tiredness} isAnimationActive={false} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {weeklyResponseData.length > 0 && (
            <div
              ref={engagementRef}
              className="chart-render-engagement"
              style={isLandscape ? { width: 1400, height: 182 } : undefined}
            >
              <EngagementLineChart data={weeklyResponseData} height={isLandscape ? 165 : 185} />
            </div>
          )}

          {individualTeamData.map(teamData => (
            <div
              key={teamData.teamId}
              ref={el => teamChartRefs.current[teamData.teamId] = el}
              className="chart-render-team-full"
              style={isLandscape ? { width: 1400, height: 384 } : undefined}
            >
              <div className="team-chart-row">
                <div className="speedometer-pair">
                  <SpeedDialPDF value={teamData.avgHappiness} title="Happiness: Last 2 Completed Weeks" type="happiness" />
                  <SpeedDialPDF value={teamData.avgTiredness} title="Tiredness: Last 2 Completed Weeks" type="tiredness" />
                </div>
                <div className="team-line-chart-container">
                  <div className="team-line-chart-header">
                    <span className="team-line-chart-title">{isMonthly ? 'Monthly Trend' : 'Weekly Trend'}</span>
                    <div className="team-line-chart-legend">
                      <span className="legend-item"><span className="legend-dot happiness"></span>Happiness</span>
                      <span className="legend-item"><span className="legend-dot tiredness"></span>Tiredness</span>
                    </div>
                  </div>
                  <TeamLineChart data={teamData.lineData} height={isLandscape ? 335 : 250} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default ReportsSection;
